import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Grid,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { automationAPI, agentsAPI, teamsAPI, labelsAPI } from '../../services/api';

interface AutomationRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  ruleId?: number | null;
}

const EVENT_TYPES = [
  { value: 'conversation_created', label: 'Conversation Created' },
  { value: 'conversation_updated', label: 'Conversation Updated' },
  { value: 'message_created', label: 'Message Created' },
  { value: 'conversation_opened', label: 'Conversation Opened' }
];

const OPERATORS = [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' }
];

const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'assignee_id', label: 'Assignee' },
  { value: 'team_id', label: 'Team' },
  { value: 'inbox_id', label: 'Inbox' },
  { value: 'message_content', label: 'Message Content' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_phone', label: 'Contact Phone' }
];

const ACTION_TYPES = [
  { value: 'assign_agent', label: 'Assign to Agent' },
  { value: 'assign_team', label: 'Assign to Team' },
  { value: 'remove_assignee', label: 'Remove Assignee' },
  { value: 'add_label', label: 'Add Label' },
  { value: 'remove_label', label: 'Remove Label' },
  { value: 'change_status', label: 'Change Status' },
  { value: 'change_priority', label: 'Change Priority' },
  { value: 'resolve_conversation', label: 'Resolve Conversation' },
  { value: 'send_message', label: 'Send Message' }
];

const AutomationRuleDialog: React.FC<AutomationRuleDialogProps> = ({
  open,
  onClose,
  onSave,
  ruleId
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_event: 'conversation_created',
    enabled: true
  });

  const [conditions, setConditions] = useState<any[]>([
    { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' }
  ]);

  const [actions, setActions] = useState<any[]>([
    { action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }
  ]);

  const [agents, setAgents] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadResources();
      if (ruleId) {
        loadRule();
      } else {
        resetForm();
      }
    }
  }, [open, ruleId]);

  const loadResources = async () => {
    try {
      const [agentsData, teamsData, labelsData] = await Promise.all([
        agentsAPI.getAgents().catch(() => ({ agents: [] })),
        teamsAPI.getTeams().catch(() => ({ teams: [] })),
        labelsAPI.getLabels().catch(() => ({ labels: [] }))
      ]);
      setAgents(agentsData.agents || agentsData || []);
      setTeams(teamsData.teams || teamsData || []);
      setLabels(labelsData.labels || labelsData || []);
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  };

  const loadRule = async () => {
    try {
      setLoading(true);
      const rule = await automationAPI.getAutomationRule(ruleId!);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_event: rule.trigger_event,
        enabled: rule.enabled
      });
      if (rule.conditions && rule.conditions.length > 0) {
        setConditions(rule.conditions);
      }
      if (rule.actions && rule.actions.length > 0) {
        setActions(rule.actions);
      }
    } catch (err) {
      console.error('Failed to load rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_event: 'conversation_created',
      enabled: true
    });
    setConditions([
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' }
    ]);
    setActions([
      { action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }
    ]);
  };

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' }
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (index: number, field: string, value: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const handleAddAction = () => {
    setActions([
      ...actions,
      { action_type: 'assign_agent', action_parameters: {}, execution_order: actions.length }
    ]);
  };

  const handleRemoveAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleActionChange = (index: number, field: string, value: any) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setActions(newActions);
  };

  const handleActionParameterChange = (index: number, paramKey: string, paramValue: any) => {
    const newActions = [...actions];
    newActions[index] = {
      ...newActions[index],
      action_parameters: {
        ...newActions[index].action_parameters,
        [paramKey]: paramValue
      }
    };
    setActions(newActions);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        ...formData,
        conditions: conditions.map(c => ({
          ...c,
          condition_group: c.condition_group || 'default'
        })),
        actions: actions.map((a, i) => ({
          ...a,
          execution_order: a.execution_order !== undefined ? a.execution_order : i
        }))
      };

      if (ruleId) {
        await automationAPI.updateAutomationRule(ruleId, payload);
      } else {
        await automationAPI.createAutomationRule(payload);
      }

      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to save rule:', err);
      alert('Failed to save rule: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const renderActionParameters = (action: any, index: number) => {
    const params = action.action_parameters || {};

    switch (action.action_type) {
      case 'assign_agent':
        return (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={params.agent_id || ''}
              label="Agent"
              onChange={(e) => handleActionParameterChange(index, 'agent_id', e.target.value)}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'assign_team':
        return (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={params.team_id || ''}
              label="Team"
              onChange={(e) => handleActionParameterChange(index, 'team_id', e.target.value)}
            >
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'add_label':
      case 'remove_label':
        return (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Label</InputLabel>
            <Select
              value={params.label_id || ''}
              label="Label"
              onChange={(e) => handleActionParameterChange(index, 'label_id', e.target.value)}
            >
              {labels.map((label) => (
                <MenuItem key={label.id} value={label.id}>
                  {label.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'change_status':
        return (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={params.status || ''}
              label="Status"
              onChange={(e) => handleActionParameterChange(index, 'status', e.target.value)}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
        );

      case 'change_priority':
        return (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={params.priority || ''}
              label="Priority"
              onChange={(e) => handleActionParameterChange(index, 'priority', e.target.value)}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
        );

      case 'send_message':
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Message"
            value={params.message || ''}
            onChange={(e) => handleActionParameterChange(index, 'message', e.target.value)}
            sx={{ mt: 1 }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {ruleId ? 'Edit Automation Rule' : 'Create Automation Rule'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Basic Information */}
          <Typography variant="h6" gutterBottom>
            Rule Information
          </Typography>

          <TextField
            fullWidth
            label="Rule Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Trigger Event</InputLabel>
            <Select
              value={formData.trigger_event}
              label="Trigger Event"
              onChange={(e) => setFormData({ ...formData, trigger_event: e.target.value })}
            >
              {EVENT_TYPES.map((event) => (
                <MenuItem key={event.value} value={event.value}>
                  {event.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Enable Rule"
            sx={{ mt: 1 }}
          />

          <Divider sx={{ my: 3 }} />

          {/* Conditions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Conditions
            </Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddCondition}
            >
              Add Condition
            </Button>
          </Box>

          {conditions.map((condition, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Grid container spacing={2} alignItems="center">
                {index > 0 && (
                  <Grid item xs={12}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={condition.group_operator || 'AND'}
                        onChange={(e) => handleConditionChange(index, 'group_operator', e.target.value)}
                      >
                        <MenuItem value="AND">AND</MenuItem>
                        <MenuItem value="OR">OR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={condition.field_name}
                      label="Field"
                      onChange={(e) => handleConditionChange(index, 'field_name', e.target.value)}
                    >
                      {CONDITION_FIELDS.map((field) => (
                        <MenuItem key={field.value} value={field.value}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator}
                      label="Operator"
                      onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                    >
                      {OPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    value={condition.expected_value || ''}
                    onChange={(e) => handleConditionChange(index, 'expected_value', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveCondition(index)}
                    disabled={conditions.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Actions
            </Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddAction}
            >
              Add Action
            </Button>
          </Box>

          {actions.map((action, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={11}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      value={action.action_type}
                      label="Action Type"
                      onChange={(e) => handleActionChange(index, 'action_type', e.target.value)}
                    >
                      {ACTION_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {renderActionParameters(action, index)}
                </Grid>

                <Grid item xs={12} sm={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAction(index)}
                    disabled={actions.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || conditions.length === 0 || actions.length === 0}
        >
          {loading ? 'Saving...' : ruleId ? 'Update Rule' : 'Create Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutomationRuleDialog;
